import React, { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, message } from 'antd';
import styled from 'styled-components';

import {
  useGroupSlackWorkspaceQuery,
  useSaveSlackWorkspaceMutation,
  GroupSlackWorkspaceDocument,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

interface ISlackWorkspaceConfigProps {
  groupId: string;
}

/**
 * Per-group Slack WORKSPACE config (workspace-per-group rework). The group is
 * bound to a whole Slack workspace by its bot token alone — the backend
 * resolves the team id from the token (auth.test), so there is no team-id
 * input; discovery then mirrors every channel automatically.
 */
export const SlackWorkspaceConfig = memo(
  ({ groupId }: ISlackWorkspaceConfigProps) => {
    const { t } = useTranslation();
    const { data } = useGroupSlackWorkspaceQuery({
      variables: { groupId },
      fetchPolicy: 'cache-and-network',
    });
    const link = data?.slackWorkspaceLink;
    const configured = !!link?.teamId;

    const [workspaceName, setWorkspaceName] = useState('');
    const [botToken, setBotToken] = useState('');
    const [editing, setEditing] = useState(false);
    useEffect(() => {
      setWorkspaceName(link?.workspaceName || '');
    }, [link?.workspaceName]);

    const [save, { loading }] = useSaveSlackWorkspaceMutation({
      refetchQueries: [
        { query: GroupSlackWorkspaceDocument, variables: { groupId } },
      ],
      awaitRefetchQueries: true,
    });

    const onSave = useCallback(async () => {
      if (!botToken.trim()) return;
      const result = await save({
        variables: {
          groupId,
          botToken: botToken.trim(),
          workspaceName: workspaceName.trim() || null,
        },
      });
      const payload = result.data?.setSlackWorkspace;
      if (payload?.success) {
        setBotToken('');
        setEditing(false);
        message.success(t('pages.slackConfig.workspaceSaved'));
        return;
      }
      const error = payload?.customErrors?.[0];
      if (error?.code === 'slack_error') {
        message.error(t('pages.slackConfig.workspaceBadToken'));
      } else if (error?.code === 'already_in_use') {
        message.error(t('pages.slackConfig.workspaceAlreadyInUse'));
      } else if (error?.message) {
        message.error(error.message);
      }
    }, [save, groupId, botToken, workspaceName, t]);

    return (
      <Card>
        <CardTitle>{t('pages.slackConfig.workspaceTitle')}</CardTitle>
        <Hint>{t('pages.slackConfig.workspaceHint')}</Hint>
        <GuideCollapse ghost>
          <Collapse.Panel
            header={t('pages.slackConfig.workspaceGuideTitle')}
            key="guide"
          >
            <GuideList>
              <li>
                {t('pages.slackConfig.workspaceGuideStep1')}{' '}
                <GuideLink
                  href="https://api.slack.com/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  api.slack.com/apps
                </GuideLink>
              </li>
              <li>{t('pages.slackConfig.workspaceGuideStep2')}</li>
              <li>{t('pages.slackConfig.workspaceGuideStep3')}</li>
              <li>{t('pages.slackConfig.workspaceGuideStep4')}</li>
              <li>{t('pages.slackConfig.workspaceGuideStep5')}</li>
              <li>{t('pages.slackConfig.workspaceGuideStep6')}</li>
            </GuideList>
          </Collapse.Panel>
        </GuideCollapse>
        {configured && !editing ? (
          <>
            <Linked>
              ✓{' '}
              {t('pages.slackConfig.linkedWorkspace', {
                name: link?.workspaceName || link?.teamId,
                count: link?.channelCount ?? 0,
              })}{' '}
              · {t('pages.slackConfig.workspaceTeamId', { id: link?.teamId })}
            </Linked>
            {link?.teamId ? (
              <OpenWorkspaceLink
                href={`https://app.slack.com/client/${link.teamId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('pages.slackConfig.openWorkspace')}
              </OpenWorkspaceLink>
            ) : null}
            {link?.enabled === false ? (
              <Disabled>{t('pages.slackConfig.workspaceDisabled')}</Disabled>
            ) : null}
            <ChangeBtn type="button" onClick={() => setEditing(true)}>
              {t('pages.slackConfig.change')}
            </ChangeBtn>
          </>
        ) : (
          <>
            <Row>
              <Input
                value={workspaceName}
                placeholder={t('pages.slackConfig.workspaceNamePlaceholder')}
                onChange={(e) => setWorkspaceName(e.target.value)}
              />
              <Input
                value={botToken}
                type="password"
                placeholder={t('pages.slackConfig.botTokenPlaceholder')}
                onChange={(e) => setBotToken(e.target.value)}
              />
              <SaveButton
                type="button"
                disabled={loading || !botToken.trim()}
                onClick={onSave}
              >
                {t('pages.slackConfig.save')}
              </SaveButton>
              {configured ? (
                <CancelBtn
                  type="button"
                  onClick={() => {
                    setBotToken('');
                    setWorkspaceName(link?.workspaceName || '');
                    setEditing(false);
                  }}
                >
                  {t('pages.slackConfig.cancel')}
                </CancelBtn>
              ) : null}
            </Row>
            {!configured ? (
              <NotLinked>{t('pages.slackConfig.workspaceNotLinked')}</NotLinked>
            ) : null}
          </>
        )}
      </Card>
    );
  },
);

SlackWorkspaceConfig.displayName = 'SlackWorkspaceConfig';

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  min-width: 140px;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const SaveButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Linked = styled.div`
  font-size: 12px;
  color: ${Colors.SuccessDefault};
  margin-top: 8px;
`;

const NotLinked = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-top: 8px;
`;

const Disabled = styled.div`
  font-size: 12px;
  color: ${Colors.WarningDefault};
  margin-top: 8px;
`;

const GuideCollapse = styled(Collapse)`
  margin-bottom: 8px;

  .ant-collapse-header {
    padding: 4px 0 !important;
    font-size: 12px;
    color: ${Colors.Blue};
  }

  .ant-collapse-content-box {
    padding: 4px 0 8px !important;
  }
`;

const GuideList = styled.ol`
  font-size: 12px;
  color: ${Colors.Black};
  margin: 0;
  padding-left: 18px;

  li + li {
    margin-top: 4px;
  }
`;

const GuideLink = styled.a`
  color: ${Colors.Blue};
`;

const OpenWorkspaceLink = styled.a`
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: ${Colors.Blue};

  &:hover {
    text-decoration: underline;
  }
`;

const ChangeBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 4px;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Blue};
  cursor: pointer;
`;

const CancelBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Gray};
  cursor: pointer;
`;
